import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

import type { CheckinGatheringChoice } from '@/pages/attendance/checkin-calendar-gatherings';

const PERSISTED_OCCURRENCE_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const isPersistedOccurrenceId = (id: string | null | undefined): id is string =>
  typeof id === 'string' &&
  !id.startsWith('schedule:') &&
  !id.startsWith('pending:') &&
  PERSISTED_OCCURRENCE_ID_RE.test(id);

type GatheringOccurrenceKey = Pick<CheckinGatheringChoice, 'programId' | 'occurrenceDate'>;

export const findOccurrenceForGathering = async (
  gathering: GatheringOccurrenceKey,
): Promise<string | null> => {
  const params = new URLSearchParams({
    programId: gathering.programId,
    fromDate: gathering.occurrenceDate,
    toDate: gathering.occurrenceDate,
    pageSize: '1',
  });
  const result = await api.get<{ data: Array<{ id: string }> }>(`/occurrences?${params}`);
  return result.data[0]?.id ?? null;
};

/** Returns a persisted occurrence id, creating one when needed. */
export const ensureCheckinOccurrenceId = async (
  gathering: CheckinGatheringChoice,
): Promise<string> => {
  if (isPersistedOccurrenceId(gathering.occurrenceId)) {
    return gathering.occurrenceId;
  }

  const existing = await findOccurrenceForGathering(gathering);
  if (existing) return existing;

  try {
    const created = await api.post<{ id: string }>(
      `/programs/${gathering.programId}/occurrences`,
      { occurrenceDate: gathering.occurrenceDate },
    );
    return created.id;
  } catch (err) {
    if (!(err instanceof ApiError) || err.code !== 'OCCURRENCE_DATE_TAKEN') throw err;
    const resolved = await findOccurrenceForGathering(gathering);
    if (!resolved) throw err;
    return resolved;
  }
};
